import { ReactNode } from 'react';
import { GridAlignment, SpaceVariant } from '../Common/types';
import {
  GridContainerTemplate,
  StyledGrid,
  StyledGridSizeContainer,
} from './StyledGrid';
import {
  AccessibilityProps,
  mergeResponsiveProps,
  resolveResponsiveProp,
  ResponsiveConditions,
  ResponsiveProp,
  ResponsiveStyle,
  spaceVariantToValue,
} from '../Common/boxUtils';
import { toTrackCount } from './trackCount';

export type GridColumnTracks = {
  minWidth: string;
  max?: number;
  repeat?: 'fill' | 'fit';
};

export type GridContainerWidth = `${number}px`;

export type GridContainerColumns = {
  container: { readonly [minWidth: GridContainerWidth]: number };
};

export type GridColumns =
  | ResponsiveProp<number>
  | GridColumnTracks
  | GridContainerColumns;

export type GridProps = {
  children?: ReactNode;
  /**
   * Column configuration.
   * - `number` — fixed equal-width columns, e.g. `columns={3}`
   * - `ResponsiveConditions<number>` — a fixed column count per breakpoint,
   *   e.g. `columns={{ mobile: 1, desktop: 3 }}`
   * - `{minWidth, max?, repeat?}` — columns that reflow based on a minimum
   *   track width. `repeat: 'fill'` (default) keeps empty tracks for
   *   consistent widths; `'fit'` collapses them so items stretch. `max` caps
   *   the number of columns.
   * - `{container}` — a column count from each width of the Grid up, e.g.
   *   `{ container: { '456px': 2, '928px': 4 } }` gives 1, 2 or 4 columns.
   *   Columns split the width evenly whatever their content. The Grid is
   *   wrapped in a full-width `inline-size` query container, which takes no
   *   width from its content, so it collapses in a parent that sizes to fit
   *   its children.
   *
   * Counts are floored to whole columns; anything below one column renders a
   * single column.
   */
  columns?: GridColumns;
  /** Spacing between all grid items (both rows and columns). */
  gap?: ResponsiveProp<SpaceVariant>;
  /**
   * Spacing between rows. Overrides `gap` for the row axis, per breakpoint
   * when responsive.
   */
  rowGap?: ResponsiveProp<SpaceVariant>;
  /**
   * Spacing between columns. Overrides `gap` for the column axis, per
   * breakpoint when responsive.
   */
  columnGap?: ResponsiveProp<SpaceVariant>;
  /**
   * Height of each implicit row track (`grid-auto-rows`). Combine with
   * `GridSpan rows={N}` for masonry-style layouts.
   */
  rowHeight?: ResponsiveProp<string>;
  /** `'full'` fills the parent's height. */
  height?: 'full';
  /** Vertical alignment of items within their cells (`align-items`). */
  align?: ResponsiveProp<GridAlignment>;
  /** Horizontal alignment of items within their cells (`justify-items`). */
  justify?: ResponsiveProp<GridAlignment>;
  /**
   * With `'ul'` or `'ol'`, wrap every child in `<GridSpan as="li">` — a `div`
   * directly inside a list is invalid HTML.
   */
  as?: 'div' | 'section' | 'main' | 'ul' | 'ol';
} & AccessibilityProps;

/**
 * Caps the column *count* while letting the columns that are present stretch to
 * fill the row.
 *
 * The cap has to live on the track's min size: the auto-repeat count is derived
 * from the track's minimum, so a track max cannot limit it. The max stays `1fr`
 * so that whenever fewer than `maxColumns` columns fit they still grow to fill
 * the row. `max(minWidth, …)` keeps an explicit `minWidth` honored, and
 * `min(100%, …)` lets a lone column shrink to a container narrower than
 * `minWidth` instead of overflowing it.
 */
const isNonZeroLength = (value: string | null | undefined): value is string => {
  if (!value) return false;
  return parseFloat(value) !== 0;
};

const buildCappedColumnTemplate = (
  minWidth: string,
  maxColumns: number,
  repeatMode: 'auto-fill' | 'auto-fit',
  columnGapValue: string | null | undefined
): string => {
  // `space.none` is a unitless '0', and subtracting a <number> from a
  // <percentage> makes the whole calc() invalid, so skip the gap term for any
  // zero-length gap.
  const perColumn = isNonZeroLength(columnGapValue)
    ? `calc((100% - ${maxColumns - 1} * ${columnGapValue}) / ${maxColumns})`
    : `calc(100% / ${maxColumns})`;
  const trackMin = `min(100%, max(${minWidth}, ${perColumn}))`;
  return `repeat(${repeatMode}, minmax(${trackMin}, 1fr))`;
};

const SINGLE_COLUMN = '1fr';
const EVEN_COLUMN = 'minmax(0, 1fr)';

const repeatTrack = (columns: number, track: string): string => {
  const count = toTrackCount(columns);
  if (count === null) return track;
  return `repeat(${count}, ${track})`;
};

const fixedColumnsTemplate = (columns: number): string =>
  repeatTrack(columns, SINGLE_COLUMN);

const columnCountToTemplate = (columns: number | undefined): string | null => {
  if (columns === undefined) return null;
  return fixedColumnsTemplate(columns);
};

const resolveColumnTracksTemplate = (
  { minWidth, max, repeat }: GridColumnTracks,
  columnGapValue: ResponsiveStyle<string> | undefined
): ResponsiveStyle<string> => {
  const repeatMode = repeat === 'fit' ? 'auto-fit' : 'auto-fill';
  const maxColumns = max === undefined ? null : toTrackCount(max);

  if (maxColumns === null) {
    return `repeat(${repeatMode}, minmax(${minWidth}, ${SINGLE_COLUMN}))`;
  }

  if (Array.isArray(columnGapValue)) {
    return columnGapValue.map((gapValue, index) =>
      gapValue === null && index > 0
        ? null
        : buildCappedColumnTemplate(minWidth, maxColumns, repeatMode, gapValue)
    ) as ResponsiveStyle<string>;
  }

  return buildCappedColumnTemplate(
    minWidth,
    maxColumns,
    repeatMode,
    columnGapValue
  );
};

const isContainerColumns = (
  columns: GridColumns | undefined
): columns is GridContainerColumns =>
  typeof columns === 'object' && 'container' in columns;

const toContainerTemplates = ({
  container,
}: GridContainerColumns): GridContainerTemplate[] =>
  Object.entries(container)
    .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
    .map(([minWidth, columns]) => ({
      minWidth,
      template: repeatTrack(columns, EVEN_COLUMN),
    }));

const resolveResponsiveColumnsTemplate = (
  columns: ResponsiveConditions<number>
): ResponsiveStyle<string> => [
  columnCountToTemplate(columns.mobile) ?? SINGLE_COLUMN,
  columnCountToTemplate(columns.tablet),
  columnCountToTemplate(columns.desktop),
  columnCountToTemplate(columns.desktopLarge),
  columnCountToTemplate(columns.desktopXLarge),
];

const resolveGridTemplateColumns = (
  columns: GridColumns | undefined,
  columnGapValue: ResponsiveStyle<string> | undefined
): ResponsiveStyle<string> => {
  if (columns === undefined) return SINGLE_COLUMN;
  if (typeof columns === 'number') return fixedColumnsTemplate(columns);
  if (isContainerColumns(columns)) return EVEN_COLUMN;
  if ('minWidth' in columns) {
    return resolveColumnTracksTemplate(columns, columnGapValue);
  }
  return resolveResponsiveColumnsTemplate(columns);
};

export const Grid = ({
  children,
  columns,
  gap,
  rowGap,
  columnGap,
  rowHeight,
  height,
  align,
  justify,
  ...rest
}: GridProps) => {
  const rowGapValue = spaceVariantToValue(mergeResponsiveProps(rowGap, gap));
  const columnGapValue = spaceVariantToValue(
    mergeResponsiveProps(columnGap, gap)
  );
  const gridTemplateColumns = resolveGridTemplateColumns(
    columns,
    columnGapValue
  );
  const containerTemplates = isContainerColumns(columns)
    ? toContainerTemplates(columns)
    : [];

  const grid = (
    <StyledGrid
      $gridTemplateColumns={gridTemplateColumns}
      $containerTemplates={containerTemplates}
      $gridAutoRows={resolveResponsiveProp(rowHeight)}
      $height={height}
      $rowGap={rowGapValue}
      $columnGap={columnGapValue}
      $align={resolveResponsiveProp<GridAlignment, GridAlignment>(align)}
      $justifyItems={resolveResponsiveProp<GridAlignment, GridAlignment>(
        justify
      )}
      {...rest}
    >
      {children}
    </StyledGrid>
  );

  if (containerTemplates.length === 0) return grid;
  return (
    <StyledGridSizeContainer $height={height}>{grid}</StyledGridSizeContainer>
  );
};
